import Dialog from '@mui/material/Dialog'
import { palettePrimaryMain } from '@/theme/designTokens'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import { UserStatusBadge } from './UserStatusBadge'
import { useTeamMemberById } from '../api/useTeamMemberById'
import { mapTeamMemberDtoToMemberDetails } from '../api/teamMembers.mappers'

interface Props {
  open: boolean
  onClose: () => void
  /** Tenant user id (`TeamMemberDto._id`) from the list row. */
  memberId: string | null
}

const labelSx = {
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#4a5565',
  whiteSpace: 'nowrap',
} as const

const valueSx = {
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '-0.1504px',
  color: '#23262f',
  whiteSpace: 'nowrap',
} as const

interface InfoRowProps {
  label: string
  children: React.ReactNode
}

function InfoRow({ label, children }: Readonly<InfoRowProps>) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        pt: '12px',
        pb: '1px',
        borderBottom: '1px solid #e5e7eb',
        minHeight: '53px',
        width: '100%',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ ...labelSx, pl: '26px' }}>{label}</Typography>
      {children}
    </Box>
  )
}

export function ViewMemberModal({ open, onClose, memberId }: Readonly<Props>) {
  const { data, isLoading, isError, error, refetch } = useTeamMemberById(memberId, open)

  const member = data ? mapTeamMemberDtoToMemberDetails(data) : null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: '10px',
          boxShadow:
            '0px 20px 25px 0px rgba(0,0,0,0.1), 0px 8px 10px 0px rgba(0,0,0,0.1)',
          width: 513,
          m: 0,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          px: '24px',
          pt: '24px',
          pb: '1px',
          borderBottom: '1px solid #e5e7eb',
          minHeight: '101px',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '28px',
              letterSpacing: '-0.4492px',
              color: '#23262f',
              whiteSpace: 'nowrap',
            }}
          >
            Team Member Details
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '-0.1504px',
              color: '#83888f',
              whiteSpace: 'nowrap',
            }}
          >
            View complete information about this team member.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close dialog"
          sx={{
            p: 0,
            mt: '4px',
            color: '#83888f',
            '&:hover': { bgcolor: 'transparent', color: '#23262f' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          pt: '24px',
          px: '24px',
          flexShrink: 0,
          minHeight: 200,
        }}
      >
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        )}
        {isError && (
          <Alert severity="error" action={<Button onClick={() => refetch()}>Retry</Button>}>
            {error instanceof Error ? error.message : 'Failed to load member.'}
          </Alert>
        )}
        {!isLoading && !isError && member && (
          <>
            <InfoRow label="User ID">
              <Typography sx={valueSx}>{member.id}</Typography>
            </InfoRow>
            <InfoRow label="First Name">
              <Typography sx={valueSx}>{member.firstName}</Typography>
            </InfoRow>
            <InfoRow label="Last Name">
              <Typography sx={valueSx}>{member.lastName}</Typography>
            </InfoRow>
            <InfoRow label="Email Address">
              <Typography sx={valueSx}>{member.email}</Typography>
            </InfoRow>
            <InfoRow label="Join Date">
              <Typography sx={valueSx}>{member.joinDate}</Typography>
            </InfoRow>
            <InfoRow label="User Role">
              <Typography sx={valueSx}>{member.role}</Typography>
            </InfoRow>
            <InfoRow label="Status">
              <UserStatusBadge status={member.status} />
            </InfoRow>
            {member.campaignName !== undefined && (
              <InfoRow label="Campaign Name">
                <Typography sx={valueSx}>{member.campaignName}</Typography>
              </InfoRow>
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          borderTop: '1px solid #e5e7eb',
          height: '89px',
          pr: '24px',
          pt: '1px',
          flexShrink: 0,
          mt: '24px',
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          disableElevation
          sx={{
            bgcolor: palettePrimaryMain,
            borderRadius: '4px',
            px: '16px',
            py: '10px',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.1px',
            color: '#f8fafc',
            textTransform: 'none',
            height: '40px',
            minWidth: 'auto',
            '&:hover': { bgcolor: '#0077b3' },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  )
}
